import { StackScreenProps } from "@react-navigation/stack";
import { CreateLlmStrategyRootScreenParamList } from "../../../../types";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import { LlmStrategyFormValues } from "../../components/LlmStrategyForm";
import {
  Button,
  Page,
  Stack,
  TextField,
  Card,
  Text,
  Divider,
  FlatActionList,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { Colors, View } from "react-native-ui-lib";
import { MUTATION_CREATE_LLM_STRATEGY } from "../../common";
import { useMutation } from "@apollo/client";
import { AiStrategy } from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { Switch } from "react-native";

export interface CreateLlmStrategyScreenProps
  extends StackScreenProps<
    CreateLlmStrategyRootScreenParamList,
    "CreateLlmStrategyScreen"
  > {}

export const CreateLlmStrategyScreen = ({
  navigation,
}: CreateLlmStrategyScreenProps) => {
  const { t } = useTranslation("settings");

  const [m] = useMutation<{
    createAiStrategy: AiStrategy;
  }>(MUTATION_CREATE_LLM_STRATEGY);

  const { control, reset, formState, handleSubmit, watch } =
    useFormContext<LlmStrategyFormValues>();

  const watchedCode = watch("code");
  const watchedOpenAiModel = watch("openAiConfig.model");
  const watchedAnthropicModel = watch("anthropicConfig.anthropicModel");
  const watchedGeminiModel = watch("geminiConfig.geminiModel");
  const watchedOpenAiDalleModel = watch("openAiDalleConfig.openAiDalleModel");
  const flashMessagesContext = useFlashMessages();

  const {
    isDirty,
    isLoading,
    isSubmitting,
    isValid,
    isSubmitSuccessful,
    errors,
  } = formState;

  React.useEffect(() => {
    if (isSubmitSuccessful) {
      reset(undefined, {
        keepValues: true,
      });
    }
  }, [isSubmitSuccessful, reset]);

  const handleOnSubmit = async (input: LlmStrategyFormValues) => {
    const code = input.code;

    let variables: any = {
      name: input.name,
      setDefault: input.makeDefault || false,
    };

    if (code === "openai") {
      variables.options = {
        code: "openai",
        arguments: [
          {
            value: input?.openAiConfig?.model ?? "",
            name: "model",
          },
          {
            value: input?.openAiConfig?.apiKey ?? "",
            name: "apiKey",
          },
        ],
      };
    }

    if (code === "anthropic") {
      variables.options = {
        code: "anthropic",
        arguments: [
          {
            value: input?.anthropicConfig?.anthropicModel ?? "",
            name: "model",
          },
          {
            value: input?.anthropicConfig?.anthropicApiKey ?? "",
            name: "apiKey",
          },
        ],
      };
    }

    if (code === "gemini") {
      variables = {
        ...variables,
        options: {
          code: "gemini",
          arguments: [
            {
              value: input?.geminiConfig?.geminiModel ?? "",
              name: "model",
            },
            {
              value: input?.geminiConfig?.geminiApiKey ?? "",
              name: "apiKey",
            },
          ],
        },
      };
    }

    if (code === "openai_dalle") {
      variables = {
        ...variables,
        options: {
          code: "openai_dalle",
          arguments: [
            {
              value: input?.openAiDalleConfig?.openAiDalleModel ?? "",
              name: "model",
            },
            {
              value: input?.openAiDalleConfig?.openAiDalleApiKey ?? "",
              name: "apiKey",
            },
          ],
        },
      };
    }

    return m({
      variables: {
        input: variables,
      },
    })
      .then(() => {
        flashMessagesContext.showMessage({
          message: "AI Strategy created successfully",
          severity: "success",
        });
        navigation.goBack();
      })
      .catch((error) => {
        console.error("Error creating LLM Strategy:", error);
      });
  };

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.create_llm_strategy")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  let strategyConfigMarkup;

  switch (watchedCode) {
    case "openai":
      strategyConfigMarkup = (
        <Stack vertical>
          <Controller
            control={control}
            name="openAiConfig.apiKey"
            render={({ field: { onChange, value } }) => (
              <TextField
                label="API Key"
                value={value}
                onChangeText={onChange}
              />
            )}
          />
          <Divider marginVertical={0} />
          <FlatActionList
            items={[
              {
                key: "openai_model",
                title: t("form.openai_model.label"),
                subTitle: t("form.openai_model.description"),
                onPress: () => navigation.navigate("SelectOpenAiModelScreen"),
                componentRight: () =>
                  watchedOpenAiModel ? (
                    <Text {...textButtonProps}>{watchedOpenAiModel}</Text>
                  ) : (
                    <Text {...textButtonProps}>
                      {t("form.openai_model.placeholder")}
                    </Text>
                  ),
              },
            ]}
          />
        </Stack>
      );
      break;
    case "anthropic":
      strategyConfigMarkup = (
        <Stack vertical>
          <Controller
            key={"anthropicApiKey"}
            control={control}
            name="anthropicConfig.anthropicApiKey"
            render={({ field: { onChange, value } }) => (
              <TextField
                label="Anthropic API Key"
                value={value}
                onChangeText={onChange}
              />
            )}
          />
          <Divider marginVertical={0} />
          <FlatActionList
            items={[
              {
                key: "anthropic_model",
                title: t("form.llm_strategy_anthropic_model.label"),
                subTitle: t("form.llm_strategy_anthropic_model.description"),
                onPress: () =>
                  navigation.navigate("SelectAnthropicModelScreen"),
                componentRight: () =>
                  watchedAnthropicModel ? (
                    <Text {...textButtonProps}>{watchedAnthropicModel}</Text>
                  ) : (
                    <Text {...textButtonProps}>
                      {t("form.llm_strategy_anthropic_model.placeholder")}
                    </Text>
                  ),
              },
            ]}
          />
        </Stack>
      );
      break;
    case "gemini":
      strategyConfigMarkup = (
        <Stack vertical>
          <Controller
            key={"geminiApiKey"}
            control={control}
            name="geminiConfig.geminiApiKey"
            render={({ field: { onChange, value } }) => (
              <TextField
                label="Gemini API Key"
                value={value}
                onChangeText={onChange}
              />
            )}
          />
          <Divider marginVertical={0} />
          <FlatActionList
            items={[
              {
                key: "gemini_model",
                title: t("form.llm_strategy_gemini_model.label"),
                subTitle: t("form.llm_strategy_gemini_model.description"),
                onPress: () => navigation.navigate("SelectGeminiModelScreen"),
                componentRight: () =>
                  watchedGeminiModel ? (
                    <Text {...textButtonProps}>{watchedGeminiModel}</Text>
                  ) : (
                    <Text {...textButtonProps}>
                      {t("form.llm_strategy_gemini_model.placeholder")}
                    </Text>
                  ),
              },
            ]}
          />
        </Stack>
      );
      break;
    case "openai_dalle":
      strategyConfigMarkup = (
        <Stack vertical>
          <Controller
            key={"openAiDalleApiKey"}
            control={control}
            name="openAiDalleConfig.openAiDalleApiKey"
            render={({ field: { onChange, value } }) => (
              <TextField
                label="OpenAI DALL·E API Key"
                value={value}
                onChangeText={onChange}
              />
            )}
          />
          <Divider marginVertical={0} />
          <FlatActionList
            items={[
              {
                key: "openai_dalle_model",
                title: t("form.llm_strategy_openai_dalle_model.label"),
                subTitle: t("form.llm_strategy_openai_dalle_model.description"),
                onPress: () =>
                  navigation.navigate("SelectOpenAiDalleModelScreen"),
                componentRight: () =>
                  watchedOpenAiDalleModel ? (
                    <Text {...textButtonProps}>{watchedOpenAiDalleModel}</Text>
                  ) : (
                    <Text {...textButtonProps}>
                      {t("form.llm_strategy_openai_dalle_model.placeholder")}
                    </Text>
                  ),
              },
            ]}
          />
        </Stack>
      );
      break;
    default:
      strategyConfigMarkup = null;
  }

  return (
    <Page
      title="Create AI Strategy"
      secondaryTitle="Define a new AI strategy for the system"
      footer={footerMarkup}
    >
      <Stack vertical>
        <Card title="Details">
          <Stack vertical>
            <Controller
              control={control}
              name="name"
              render={({ field: { onChange, value } }) => (
                <TextField label="Name" value={value} onChangeText={onChange} />
              )}
            />
            <Divider marginVertical={0} />
            <FlatActionList
              items={[
                {
                  key: "llm_strategy_code",
                  title: t("form.llm_strategy_code.label"),
                  subTitle: t("form.llm_strategy_code.description"),
                  onPress: () => navigation.navigate("SelectLlmTypeScreen"),
                  componentRight: () =>
                    watchedCode ? (
                      <Text {...textButtonProps}>{watchedCode}</Text>
                    ) : (
                      <Text {...textButtonProps}>
                        {t("form.llm_strategy_code.placeholder")}
                      </Text>
                    ),
                },
              ]}
            />
            <Divider marginVertical={0} />
            <FlatActionList
              items={[
                {
                  key: "llm_strategy_make_default",
                  title: t("form.llm_strategy_make_default.label"),
                  subTitle: t("form.llm_strategy_make_default.description"),
                  isAction: false,
                  componentRight: () => (
                    <Controller
                      name="makeDefault"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <View>
                          <Switch
                            value={value}
                            onValueChange={() => {
                              onChange(!value);
                            }}
                          />
                        </View>
                      )}
                    />
                  ),
                },
              ]}
            />
          </Stack>
        </Card>
        {strategyConfigMarkup && (
          <Card title="Configuration">{strategyConfigMarkup}</Card>
        )}
      </Stack>
    </Page>
  );
};

const textButtonProps = {
  fontWeight: "bold",
  color: "action",
  variant: "body",
} as any;
